/**
 * Kategorie von Hand zuweisen — dort, wo der Probelauf „bekommt keine Kategorie" meldet.
 *
 * 🔴 **Der Auslöser (Matze, 22.09.2026):** „Wie kann ich in diesem Step eine Kategorie
 * vergeben, wenn automatisch keine gefunden wurde?" — Gar nicht. Die Meldung benannte
 * ein Problem, ohne einen Ausweg zu bieten.
 *
 * 🔑 **Die eigentliche Entscheidung ist nicht „welche Kategorie", sondern „nur hier
 * oder als Regel".** Deshalb steht die Frage direkt neben dem Speichern-Knopf und nicht
 * in einer Einstellung: Wer einmal weiß, dass „Trainingsleibchen" auf Kategorie 247
 * gehört, soll das nicht bei jedem Katalog neu eintragen. Rund 90 Dateiwerte warten
 * auf genau diese Entscheidung.
 */
import { useEffect, useMemo, useState } from 'react';
import { FolderTree, Loader2, Search } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { Button } from '@libero/core/components/ui/button';

export interface KategorieOption {
  categoryId: number;
  name: string;
  pfad: string;
  istBlatt: boolean;
}

interface Props {
  entwurfId: number;
  artikelSchluessel: string;
  artikelName: string;
  /** Der Wert aus der Quelldatei — er trägt die Regel. Leer = keine Regel möglich. */
  dateiwert?: string | null;
  /** Was der Artikel schon hat. */
  aktuell: number[];
  /** Nach dem Speichern: die Liste neu laden. */
  aufGespeichert: (meldung: string) => void;
  aufAbbruch: () => void;
}

export default function KategorieWahl({
  entwurfId, artikelSchluessel, artikelName, dateiwert, aktuell, aufGespeichert, aufAbbruch,
}: Props) {
  const [optionen, setOptionen] = useState<KategorieOption[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [suche, setSuche] = useState('');
  const [gewaehlt, setGewaehlt] = useState<number[]>(aktuell);
  const [alsRegel, setAlsRegel] = useState(true);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const antwort = await api.get<{ data: KategorieOption[] }>(
          '/programmes/artikelimport/kategorien',
        );
        setOptionen(antwort.data.data);
      } catch (err: any) {
        setFehler(err?.response?.data?.error?.message ?? 'Kategorien nicht ladbar.');
      } finally {
        setLaedt(false);
      }
    })();
  }, []);

  /**
   * Gesucht wird im vollen Pfad, nicht nur im Namen — „Shorts" gibt es dreimal im Baum
   * (43, 248, 251) und „Zubehör" zweimal. Ohne den Pfad wäre die Auswahl ein Ratespiel.
   */
  const treffer = useMemo(() => {
    const begriff = suche.trim().toLowerCase();
    const liste = begriff.length === 0
      ? optionen
      : optionen.filter(o => `${o.pfad} ${o.name}`.toLowerCase().includes(begriff));
    return liste.slice(0, 60);
  }, [optionen, suche]);

  const umschalten = (id: number) => setGewaehlt(alt =>
    (alt.includes(id) ? alt.filter(x => x !== id) : [...alt, id]));

  const speichern = async () => {
    setSpeichert(true);
    setFehler(null);
    try {
      const antwort = await api.patch<{ data: { meldung: string; abgelehnt: Array<{ grund: string }> } }>(
        `/programmes/artikelimport/entwuerfe/${entwurfId}/kategorie`,
        { artikel: artikelSchluessel, kategorien: gewaehlt, alsRegel: alsRegel && !!dateiwert },
      );
      const daten = antwort.data.data;
      const abgelehnt = daten.abgelehnt ?? [];
      aufGespeichert(
        abgelehnt.length > 0
          ? `${daten.meldung} ⚠ ${abgelehnt.length} nicht übernommen: ${abgelehnt.map(a => a.grund).join(' · ')}`
          : daten.meldung,
      );
    } catch (err: any) {
      setFehler(err?.response?.data?.error?.message ?? 'Speichern fehlgeschlagen.');
    } finally {
      setSpeichert(false);
    }
  };

  return (
    <div className="space-y-3 rounded border border-[#1A1A1A] bg-white p-4">
      <div>
        <h4 className="flex items-center gap-2 font-medium text-[#1A1A1A]">
          <FolderTree className="h-4 w-4" /> Kategorie für „{artikelName}"
        </h4>
        <p className="mt-1 text-xs text-[#1A1A1A]/70">
          Das Tool legt in Plenty <strong>keine</strong> Kategorien an — es ordnet nur zu.
          Wähle eine bestehende.
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-[#1A1A1A]/40" />
        <input
          type="text"
          value={suche}
          onChange={e => setSuche(e.target.value)}
          placeholder={'Suchen — im ganzen Pfad, z.B. Trikot oder Bälle › Zubehör'}
          className="w-full rounded border border-[#DAD9D9] py-2 pl-8 pr-3 text-sm"
          autoFocus
        />
      </div>

      {laedt && (
        <p className="flex items-center gap-2 text-sm text-[#1A1A1A]/60">
          <Loader2 className="h-4 w-4 animate-spin" /> Kategoriebaum wird geladen …
        </p>
      )}

      {!laedt && (
        <div className="max-h-64 space-y-1 overflow-y-auto rounded border border-[#DAD9D9] p-2">
          {treffer.map(o => (
            <label
              key={o.categoryId}
              className={`flex cursor-pointer items-start gap-2 rounded px-2 py-1 text-sm ${
                gewaehlt.includes(o.categoryId) ? 'bg-[#E9FF57]/40' : 'hover:bg-[#DAD9D9]/30'
              }`}
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={gewaehlt.includes(o.categoryId)}
                onChange={() => umschalten(o.categoryId)}
              />
              <span className="flex-1">
                <span className="font-medium">{o.name}</span>
                <span className="ml-2 text-xs text-[#1A1A1A]/50">{o.pfad}</span>
                {!o.istBlatt && (
                  <span className="ml-2 text-xs text-amber-700">Sammelknoten</span>
                )}
              </span>
              <span className="tabular-nums text-xs text-[#1A1A1A]/40">{o.categoryId}</span>
            </label>
          ))}
          {treffer.length === 0 && (
            <p className="px-2 py-1 text-sm text-[#1A1A1A]/60">Kein Treffer.</p>
          )}
          {!laedt && suche.trim().length === 0 && optionen.length > 60 && (
            <p className="px-2 py-1 text-xs text-[#1A1A1A]/50">
              … {optionen.length - 60} weitere. Tippe, um zu suchen.
            </p>
          )}
        </div>
      )}

      {/*
        Die eigentliche Entscheidung. Sie steht bewusst hier und nicht in einer
        Einstellung — im Moment der Zuweisung weiß man, ob der Fall einmalig ist.
      */}
      {dateiwert
        ? (
          <label className="flex cursor-pointer items-start gap-2 rounded bg-[#DAD9D9]/30 p-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={alsRegel}
              onChange={e => setAlsRegel(e.target.checked)}
            />
            <span>
              Als <strong>Regel</strong> für alle Artikel mit dem Dateiwert{' '}
              <code className="rounded bg-white px-1">{dateiwert}</code> merken.
              <span className="block text-xs text-[#1A1A1A]/60">
                Gilt ab sofort — auch für die übrigen Artikel dieses Entwurfs, die noch
                keine Kategorie haben, und für jeden künftigen Lauf. Ohne Haken gilt die
                Zuweisung nur für diesen einen Artikel.
              </span>
            </span>
          </label>
        )
        : (
          <p className="rounded bg-[#DAD9D9]/30 p-2 text-xs text-[#1A1A1A]/70">
            Die Quelldatei führt für diesen Artikel keinen Kategoriewert — eine Regel ist
            deshalb nicht möglich. Die Zuweisung gilt nur für diesen Artikel.
          </p>
        )}

      {fehler && <p className="text-sm text-red-700">{fehler}</p>}

      <div className="flex items-center gap-2">
        <Button
          onClick={() => void speichern()}
          disabled={speichert || laedt}
          className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
        >
          {speichert
            ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Wird gespeichert …</>
            : `${gewaehlt.length} Kategorie${gewaehlt.length === 1 ? '' : 'n'} übernehmen`}
        </Button>
        <Button variant="outline" onClick={aufAbbruch} disabled={speichert}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
}
